import React, { FC, useMemo } from "react";
import { Segment } from "semantic-ui-react";
import _ from "lodash";
import type { Annotations, Data, Datum, Layout, ModeBarButtonAny, PlotData } from "plotly.js";

import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import type { BoxStats } from "../../../../apis/types/analysisTypes";
import { BOX_LOWER_FILL_COLOR, BOX_UPPER_FILL_COLOR, TRANSPARENT_COLOR } from "../../../../utilities/graphics";
import { getColorFromPalette } from "../../Trials/MFASummary/MapStyles";
import { AXIS_TICK_SIZE, AXIS_TITLE_SIZE, CHART_TITLE_SIZE, MAX_LABEL_LEN } from "./constants";
import styles from "./BoxWhiskerChart.module.css";

export type { BoxStats };

/** One box slot on the x-axis, e.g. a treatment. */
export type BoxWhiskerCategory = {
	/** The x-axis category, e.g. String(trialTreatmentId). */
	id: string;
	/** The five-number summary of the box. NaN leaves a gap in that category. */
	stats: BoxStats;
	/**
	 * The Tukey letter. "" shows nothing for this category. Leave `undefined` on every category to
	 * draw the chart without a letter row.
	 */
	letter?: string | null;
	/** Drawn under the category when `showCategoryLabels` is set, e.g. the treatment name. */
	label?: string;
};

/** One marker, placed in a category. */
export type BoxWhiskerPoint = {
	/** The {@link BoxWhiskerCategory.id} this point sits in. */
	categoryId: string;
	y: number | null;
	/** Per-point hover values, read by {@link BoxWhiskerSeries.hoverLines} as `%{customdata[i]}`. */
	details: Datum[];
};

/** A colored group of markers: one legend entry. */
export type BoxWhiskerSeries = {
	/** The legend entry. */
	name: string;
	/** Index into the shared palette (see getColorFromPalette). */
	colorIndex: number;
	points: BoxWhiskerPoint[];
	/**
	 * Hover template lines above the stats. They can use `%{y}` and `%{customdata[i]}`, where i
	 * indexes each point's `details`. The chart appends the category's stats after these.
	 */
	hoverLines: string[];
};

/** The hover lines for a plot-level marker. Matches `details` of [plot name, treatment, replicate]. */
export const PLOT_HOVER_LINES = [
	"<b>Plot: </b>%{customdata[0]}",
	"<b>Treatment: </b>%{customdata[1]}",
	"<b>Replicate: </b>%{customdata[2]}",
	"<b>Value: </b>%{y}",
];

export type BoxWhiskerChartProps = {
	categories: BoxWhiskerCategory[];
	series: BoxWhiskerSeries[];
	yAxisTitle?: string;
	/** Drawn top-left, above the plot. */
	title?: string;
	legendTitle: string;
	/** Shows the segment's loading overlay. */
	loading?: boolean;
	/** Replaces the plot when there are no categories. */
	emptyMessage?: string;
	/** Draws every series in the first palette color and hides the legend. */
	disableColors?: boolean;
	/** Draws each category's `label` under the axis. */
	showCategoryLabels?: boolean;
	/** Horizontal marker spread, as a fraction of the box width. */
	jitter?: number;
	markerSize?: number;
	/** Box width in x-axis units. Plotly picks one when unset. */
	boxWidth?: number;
	/** The visible x-axis range, in category positions. Plotly picks one when unset. */
	xRange?: [number, number];
	/** Plot height in px, before room for category labels. */
	chartHeight?: number;
	/** Drops the segment's default vertical margin. */
	flush?: boolean;
	modeBarButtons?: ModeBarButtonAny[];
	snapshotFileName: string;
};

/**
 * A box trace with precomputed statistics. @types/plotly.js does not declare the
 * lowerfence/q1/median/q3/upperfence attributes, so they are added here.
 */
type BoxStatsTrace = Partial<PlotData> & {
	lowerfence: number[];
	q1: number[];
	median: number[];
	q3: number[];
	upperfence: number[];
};

/** @types/plotly.js declares Annotations.textangle as a string, but plotly takes a number. */
type LetterAnnotation = Omit<Partial<Annotations>, "textangle"> & { text: string; y: number; textangle: number };

const DEFAULT_CHART_HEIGHT = 400;

/** Formats a stat for the hover; blank for NaN. */
const formatStat = (value: number) => (Number.isFinite(value) ? value.toFixed(5) : "");

/**
 * A box and whisker chart with a marker overlay and Tukey letters. Each box is split at the median
 * in two shades. Callers shape their own data into categories and series, so any page can share
 * the look.
 */
const BoxWhiskerChart: FC<BoxWhiskerChartProps> = ({
	categories,
	series,
	yAxisTitle,
	title,
	legendTitle,
	loading = false,
	emptyMessage,
	disableColors = false,
	showCategoryLabels = false,
	jitter = 0,
	markerSize = 8,
	boxWidth,
	xRange,
	chartHeight = DEFAULT_CHART_HEIGHT,
	flush = false,
	// Plotly throws on an explicit undefined here, and draws nothing past the axes.
	modeBarButtons = [],
	snapshotFileName,
}) => {
	const { chartOptions, bottomExtra } = useMemo(() => {
		const boxX = categories.map((c) => c.id);
		const boxMin = categories.map((c) => c.stats.min);
		const boxQ1 = categories.map((c) => c.stats.q1);
		const boxMedian = categories.map((c) => c.stats.median);
		const boxQ3 = categories.map((c) => c.stats.q3);
		const boxMax = categories.map((c) => c.stats.max);

		// Letters. Annotation x is the 0-based index into categories, which matches Plotly's category positions.
		let letterAnnotations: LetterAnnotation[] = [];
		if (categories.some((c) => c.letter !== undefined)) {
			const letters = categories.map((c) => c.letter ?? "");
			// If every letter is "a", nothing separates the treatments, so show a dash for each lettered one.
			const displayLetters = letters.every((l) => l === "a") ? letters.map((l) => (l ? "-" : l)) : letters;
			letterAnnotations = displayLetters.map((text, idx) => ({
				x: idx,
				y: 1.06,
				yref: "paper" as const,
				text,
				showarrow: false,
				font: { size: 14 },
				textangle: -45,
			}));
		}

		const maxLabelLen = Math.max(1, ...letterAnnotations.map((a) => a.text.trim().length));
		const tooMany = maxLabelLen > MAX_LABEL_LEN;
		if (tooMany) {
			letterAnnotations.forEach((a) => {
				a.text = "-";
			});
		}
		// With a title the letters sit tight under it. Without one there is room above the plot.
		const baseAnnotationY = title ? 1.06 : 1.15;
		const baseMarginTop = title ? 60 : 90;
		const annotationY = tooMany ? baseAnnotationY : baseAnnotationY + Math.max(0, (maxLabelLen - 2) * 0.015);
		const marginTop = tooMany ? baseMarginTop : baseMarginTop + Math.max(0, (maxLabelLen - 2) * 10);
		letterAnnotations.forEach((a) => {
			a.y = annotationY;
		});

		const maxNameLength = showCategoryLabels ? Math.max(0, ...categories.map((c) => (c.label ?? "").length)) : 0;
		const labelAnnotations: Partial<Annotations>[] = showCategoryLabels
			? categories.flatMap((c, idx) =>
					c.label
						? [
								{
									x: idx,
									y: -0.12,
									yref: "paper" as const,
									xanchor: "center" as const,
									yanchor: "top" as const,
									text: c.label,
									showarrow: false,
									font: { size: 11 },
								},
							]
						: [],
				)
			: [];

		const boxBaseProps = {
			type: "box" as const,
			x: boxX,
			boxpoints: false as const,
			showlegend: false,
			width: boxWidth,
		};

		const traces: (BoxStatsTrace | Partial<PlotData>)[] = [
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxQ1,
				q3: boxMedian,
				upperfence: boxMedian,
				fillcolor: BOX_LOWER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMedian,
				q1: boxMedian,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: BOX_UPPER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: TRANSPARENT_COLOR,
				line: { color: "black" },
				hoverinfo: "skip",
			},
		];

		const statsById = _.keyBy(categories, "id");

		// The markers are invisible boxes that only draw their points, which is what allows jitter.
		for (const s of series) {
			const color = getColorFromPalette(disableColors ? 0 : s.colorIndex);
			const detailCount = s.points[0]?.details.length ?? 0;
			const statLines = ["Min", "Q1", "Median", "Q3", "Max"].map(
				(label, i) => `<b>${label}: </b>%{customdata[${detailCount + i}]}`,
			);

			traces.push({
				type: "box",
				boxpoints: "all",
				jitter,
				pointpos: 0,
				fillcolor: TRANSPARENT_COLOR,
				line: { color: TRANSPARENT_COLOR },
				width: boxWidth,
				name: s.name,
				x: s.points.map((p) => p.categoryId),
				y: s.points.map((p) => p.y),
				customdata: s.points.map((p) => {
					const stats = statsById[p.categoryId]?.stats;
					return [
						...p.details,
						...[stats?.min, stats?.q1, stats?.median, stats?.q3, stats?.max].map((v) =>
							v === undefined ? "" : formatStat(v),
						),
					];
				}),
				hovertemplate: [...s.hoverLines, "", ...statLines].join("<br>") + "<extra></extra>",
				marker: {
					color,
					symbol: "circle",
					size: markerSize,
					line: { color: "black", width: 0.5 },
				},
				showlegend: !disableColors,
			});
		}

		const bottomExtra = showCategoryLabels ? 50 + maxNameLength * 5 : 0;

		const layout: Partial<Layout> = {
			...(title ? { title: { text: title, x: 0.01, xanchor: "left", font: { size: CHART_TITLE_SIZE } } } : {}),
			xaxis: {
				title: {
					text: "Treatment",
					standoff: showCategoryLabels ? 80 + maxNameLength * 5 : 12,
					font: { size: AXIS_TITLE_SIZE },
				},
				type: "category",
				tickfont: { size: AXIS_TICK_SIZE },
				tickangle: 0,
				automargin: true,
				...(xRange ? { range: xRange } : {}),
			},
			yaxis: {
				title: { text: yAxisTitle, standoff: 10, font: { size: AXIS_TITLE_SIZE } },
				tickfont: { size: AXIS_TICK_SIZE },
				zeroline: false,
				automargin: true,
			},
			legend: {
				orientation: "h",
				title: { text: legendTitle },
				itemsizing: "constant",
				x: 1,
				xanchor: "right",
				y: showCategoryLabels ? -(0.18 + maxNameLength * 0.009) : -0.25,
				yanchor: "top",
			},
			margin: { l: yAxisTitle ? 60 : 6, r: 20, b: 80 + bottomExtra, t: marginTop, pad: 0 },
			hovermode: "closest",
			autosize: true,
			// Cast: see LetterAnnotation (textangle is mistyped in @types/plotly.js).
			annotations: [...letterAnnotations, ...labelAnnotations] as unknown as Partial<Annotations>[],
			boxmode: "overlay",
			// @ts-expect-error plotly types suck!
			boxgap: 0,
		};

		return { chartOptions: { data: traces as Data[], layout }, bottomExtra };
	}, [
		categories,
		series,
		yAxisTitle,
		title,
		legendTitle,
		disableColors,
		showCategoryLabels,
		jitter,
		markerSize,
		boxWidth,
		xRange,
	]);

	return (
		<Segment basic loading={loading} className={flush ? `${styles.container} ${styles.flush}` : styles.container}>
			{categories.length === 0 && emptyMessage ? (
				<p>{emptyMessage}</p>
			) : (
				<AFPlot
					data={chartOptions.data}
					layout={chartOptions.layout}
					config={{
						displayModeBar: true,
						modeBarButtonsToAdd: modeBarButtons,
						toImageButtonOptions: { filename: snapshotFileName },
					}}
					useResizeHandler
					style={{ width: "100%", height: `${chartHeight + bottomExtra}px` }}
				/>
			)}
		</Segment>
	);
};

export default BoxWhiskerChart;
